import React, { Ref, useEffect, useRef, useState } from 'react'
import { TalkingPoint as TalkingPointType } from './TalkingPointsEditor'
import ReactQuill from 'react-quill';
import CollapsableContent from 'Components/Common/CollapsableContent';
import DOMPurify from 'dompurify';
import { sanitizeURLs } from 'utils/helpers';
import HTMLContent from 'Components/Common/HTMLContent';
import AutoResizingInput from 'Components/Common/AutoResizingInput';

const listToHTML = (items: string[]): string => {
    return `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`;
};

const parseHTMLToList = (html: string): string[] => {
    const container = document.createElement('div');
    container.innerHTML = html;

    const items: string[] = [];
    container.querySelectorAll('ul li').forEach((li) => {
        const text = li.textContent?.trim();
        if (text) items.push(text);
    });

    return items;
};


export const TalkingPoint: React.FC<{ talkingPoint: TalkingPointType, onChange: (talkingPoint: TalkingPointType) => void, onDelete: () => void, disabled?: boolean }> = ({ talkingPoint, onChange, onDelete, disabled = false }) => {
    const [html, setHtml] = useState('');
    const [editMode, setEditMode] = useState(false);
    const quillRef = useRef<ReactQuill | null>(null);
    const [titleRef, setTitleRef] = useState<any>(null);

    useEffect(() => {
        if (Array.isArray(talkingPoint.pointers) && talkingPoint.pointers.length > 0) {
            setHtml(listToHTML(talkingPoint.pointers));
        }
    }, [talkingPoint.pointers]);

    useEffect(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const html = quill.root.innerHTML;
        const contents = quill.getContents();

        const isEmpty = contents.ops?.length === 1 && contents.ops[0].insert === '\n';
        const isParagraph = html === '<p><br></p>';

        if (isEmpty || isParagraph) {
            quill.formatLine(0, 1, { list: 'bullet' });
        }
    }, [editMode, html]);

    const handleChange = (content: string) => {
        setHtml(content);
        const items = parseHTMLToList(content);
        if (JSON.stringify(items) !== JSON.stringify(talkingPoint.pointers ?? [])) {
            onChange({ ...talkingPoint, pointers: items });
        }
    };

    useEffect(() => {
        if(!talkingPoint.title && (!Array.isArray(talkingPoint.pointers) || talkingPoint.pointers.length === 0)){
            setEditMode(true)
            if(titleRef){
                titleRef.current?.focus();
            }
        }
    },[talkingPoint, titleRef])

    const modules = {
        toolbar: [['list']],
    };

    const formats = ['list'];

    return (
        <div className="script-card">
            <div className="script-card-header">
                {
                    editMode ? <AutoResizingInput disabled={disabled} onRender={(textAreaRef) => setTitleRef(textAreaRef)} className='script-name talking-point-title-input text-start' value={talkingPoint.title} onChange={(e) => {
                        onChange({ ...talkingPoint, title: e.target.value });
                    }} /> : <span className="script-name w-100 overflow-hidden p-2 text-start">{talkingPoint.title}</span>
                }
                {
                    !disabled &&
                    <div className="script-actions">
                        {editMode ? (
                            <button className="edit-script" onClick={() => setEditMode(false)}>
                                <span>Save</span>
                            </button>
                        ) : (
                            <button className="edit-script" onClick={() => setEditMode(true)}>
                                <span>Edit</span>
                            </button>
                        )}
                        <button className="delete-script" onClick={onDelete}>
                            <span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                                    <path d="M13.6615 4.5H2.32812" stroke="#A5261F" strokeLinecap="round" />
                                    <path d="M12.5564 6.16797L12.2498 10.7674C12.1318 12.5373 12.0728 13.4223 11.4961 13.9618C10.9195 14.5013 10.0325 14.5013 8.25866 14.5013H7.74308C5.96921 14.5013 5.08228 14.5013 4.50561 13.9618C3.92893 13.4223 3.86994 12.5373 3.75194 10.7674L3.44531 6.16797" stroke="#A5261F" strokeLinecap="round" />
                                    <path d="M6.32812 7.83203L6.66146 11.1654" stroke="#A5261F" strokeLinecap="round" />
                                    <path d="M9.66146 7.83203L9.32812 11.1654" stroke="#A5261F" strokeLinecap="round" />
                                    <path d="M4.32812 4.5C4.36538 4.5 4.384 4.5 4.40089 4.49957C4.94985 4.48566 5.43414 4.13661 5.62094 3.62021C5.62668 3.60433 5.63257 3.58666 5.64435 3.55132L5.70908 3.35714C5.76433 3.19139 5.79196 3.1085 5.8286 3.03813C5.9748 2.75738 6.24529 2.56242 6.55786 2.51251C6.63621 2.5 6.72357 2.5 6.8983 2.5H9.09128C9.26601 2.5 9.35337 2.5 9.43172 2.51251C9.7443 2.56242 10.0148 2.75738 10.161 3.03813C10.1976 3.1085 10.2253 3.19138 10.2805 3.35714L10.3452 3.55132C10.357 3.58661 10.3629 3.60434 10.3686 3.62021C10.5554 4.13661 11.0397 4.48566 11.5887 4.49957C11.6056 4.5 11.6242 4.5 11.6615 4.5" stroke="#A5261F" />
                                </svg>
                            </span>
                            Delete
                        </button>
                    </div>
                }
            </div>

            <div className={`script-card-body list-editor ${editMode ? 'p-0' : ''}`}>
                {editMode ? (
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={html}
                        onChange={handleChange}
                        modules={modules}
                        formats={formats}
                        readOnly={disabled}
                        placeholder="📝 Start writing bullet points..."
                    />
                ) : (
                    <CollapsableContent>
                        <HTMLContent content={html} />
                    </CollapsableContent>
                )}
            </div>
        </div>
    );
}
